import React, { useRef, useState } from 'react';
import { IonContent, IonHeader, IonSlides, IonSlide, IonLabel, IonPage, IonGrid, IonRow, IonCol, IonButton, IonIcon, IonText } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { Gender } from '../utils/Settings';
import { arrowBackOutline } from 'ionicons/icons';
import Toolbar from '../components/Toolbar';
import { isValid } from '../utils/CVRiskUtils';
import './RenalRisk.scss';
import './CVRisk.scss';
import NumericInput from '../components/NumericInput';
import { useGA4React } from 'ga-4-react';

function ComputeGlomerularFiltration(age: number, gender: Gender, afrodescendant: boolean, creatinine: number) {
  // obtenerRiesgo: Calcula el riesgo
  // @params
  //    edad: Integer
  //    sexo : true==masculino / false==femenino
  //    afrodescendiente: true/false
  //    creatinina (rango entre 0.4 y 8)
  return 186 * Math.pow(creatinine, -1.154) * Math.pow(age, -0.203) * (afrodescendant ? 1.21 : 1) * (gender === Gender.Female ? 0.742 : 1);
}

const RenalRisk: React.FC = () => {
  const { t } = useTranslation();
  const ga = useGA4React();
  const [gender, setGender] = useState<Gender>(Gender.Male);
  const [age, setAge] = useState<number>(40);
  const [afrodescendant, setAfrodescendant] = useState<boolean>(false);
  const [creatinine, setCreatinine] = useState<number>(1.0);
  const [creatinineInitial, setCreatinineInitial] = useState<number>(1.0);
  const [creatinineIf, setCreatinineIf] = useState<number>(1.0);

  let sliderRef = useRef<HTMLIonSlidesElement>(null);

  let risk = ComputeGlomerularFiltration(age, gender, afrodescendant, creatinine);

  if (ga)
    ga.event("calculateRenalRisk", "result", risk.toString());

  function goBackForm() {
    sliderRef.current?.slidePrev();
  }

  function onCalculate() {
    setCreatinine(creatinineInitial);
    setCreatinineIf(creatinineInitial);
    sliderRef.current?.slideNext();
  }

  function onReCalculate() {
    setCreatinine(creatinineIf);
  }

  let isValidAge = isValid(age, 40, 75);
  let isValidCreatinineInitial = isValid(creatinineInitial, 0.4, 8);
  let isValidCreatinineIf = isValid(creatinineIf, 0.4, 8);

  let sliderOptions = {
    followFinger: false,
    allowTouchMove: false
  };

  let isNormalRange = risk >= 60.0;

  return (
    <IonPage id="renalRisk-page">
      <IonHeader>
        <Toolbar title={t('Glomerular Filtration')} />
      </IonHeader>
      <IonContent fullscreen>
        <IonSlides className="calculatorSlides" ref={sliderRef} pager={true} scrollbar={true} options={sliderOptions}>
          <IonSlide>
            <IonGrid className="dashboard calculatorGrid">
              <IonRow>
                <IonText color="primary"><h1 className="ion-hide-xl-up"><b>{t('Glomerular Filtration')}</b></h1></IonText>
              </IonRow>
              <IonRow>
                <IonCol size="12" size-sm="12">
                  &nbsp;
                </IonCol>
                <IonCol size-sm="6" size="12">
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('Gender')}</b></IonLabel>
                    </IonCol>
                    <IonCol>
                      <IonButton color={gender === Gender.Female ? 'primary' : 'dark'} fill={gender === Gender.Female ? 'solid' : 'outline'} onClick={() => setGender(Gender.Female)}>{t('Female')}</IonButton>
                    </IonCol>
                    <IonCol>
                      <IonButton color={gender === Gender.Male ? 'primary' : 'dark'} fill={gender === Gender.Male ? 'solid' : 'outline'} onClick={() => setGender(Gender.Male)}>{t('Male')}</IonButton>
                    </IonCol>
                  </IonRow>
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('Afrodescendant')}</b><br />&nbsp;</IonLabel>
                    </IonCol>
                    <IonCol size="4" size-sm="3">
                      <IonButton color={afrodescendant === true ? 'primary' : 'dark'} fill={afrodescendant === true ? 'solid' : 'outline'} onClick={() => setAfrodescendant(true)}>{t('Yes')}</IonButton>
                    </IonCol>
                    <IonCol size="4" size-sm="3">
                      <IonButton color={afrodescendant === false ? 'primary' : 'dark'} fill={afrodescendant === false ? 'solid' : 'outline'} onClick={() => setAfrodescendant(false)}>{t('No')}</IonButton>                    </IonCol>
                  </IonRow>
                </IonCol>
                <IonCol size-sm="5" offset-sm="1" size="12">
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('Age')}</b></IonLabel>
                    </IonCol>
                    <IonCol size="12">
                      <NumericInput value={age} min={40} max={75} onValueChange={(value: number) => setAge(value)} />
                    </IonCol>
                  </IonRow>
                  <IonRow>
                    <IonCol size="12">
                      <IonLabel><b>{t('Creatinine')}</b><br />(mg/dl)</IonLabel>
                    </IonCol>
                    <IonCol size="12">
                      <NumericInput value={creatinineInitial} min={0.4} max={8.0} onValueChange={(value: number) => setCreatinineInitial(value)} />
                    </IonCol>
                  </IonRow>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol size="12" size-sm="4">
                  <IonButton className="calculateButton" disabled={!(isValidAge && isValidCreatinineInitial)} fill="solid" onClick={onCalculate}>{t('Calculate')}</IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonSlide>
          <IonSlide>
            <IonGrid className="dashboard calculatorGrid">
              <IonRow>
                <IonText color="primary"><h1 className="ion-hide-xl-up"><b>{t('Glomerular Filtration')}</b></h1></IonText>
              </IonRow>
              <IonRow>
                <IonCol size="12" size-sm="6">
                  <IonRow>
                    <IonCol size="12">
                      <IonButton className="backButton" fill="clear" onClick={goBackForm}>
                        <IonIcon icon={arrowBackOutline} slot="icon-only" />
                      </IonButton>
                    </IonCol>
                    <IonCol size="12" className="egfrStyle">
                      <IonLabel>{t('Estimated Glomerular Filtration Rate')}({t('EGFR')})</IonLabel>
                    </IonCol>

                    <IonCol size="12" className="valueStyle">
                      <IonLabel>{risk.toFixed(2)} ml/min - {isNormalRange ? t('Normal range') : t('GF Reduced')}</IonLabel>
                    </IonCol>

                    <IonCol size="12" className="alertStyle" hidden={isNormalRange}>
                      <IonLabel>{t('GF Alert')}</IonLabel>
                    </IonCol>
                  </IonRow>
                </IonCol>
                <IonCol size="12" size-sm="5" offset-sm="1">
                  <IonLabel className="inputDataLabel">{t('InputData')}</IonLabel>
                  <table className="inputDataTable">
                    <tbody>
                      <tr>
                        <td>{t('Gender')}</td>
                        <td>{t(gender)}</td>
                      </tr>
                      <tr>
                        <td>{t('Age')}</td>
                        <td>{age}</td>
                      </tr>
                      <tr>
                        <td>{t('Afrodescendant')}</td>
                        <td>{t(afrodescendant ? 'Yes' : 'No')}</td>
                      </tr>
                      <tr>
                        <td>{t('Creatinine')}</td>
                        <td>{creatinine.toFixed(1)} (mg/dl)</td>
                      </tr>
                    </tbody>
                  </table>
                </IonCol>

                <IonCol size="12">
                  <IonLabel><b>{t('What Would Happen If')}</b></IonLabel>
                </IonCol>

                <IonCol size="12">
                  <IonLabel><b>{t('Creatinine')}</b><br />(mg/dl)</IonLabel>
                </IonCol>
                <IonCol size="12" size-sm="12">
                  <NumericInput value={creatinineIf} min={0.4} max={8.0} onValueChange={(value: number) => setCreatinineIf(value)} />
                </IonCol>
                <IonCol size="12" size-sm="4">
                  <IonButton className="calculateButton" disabled={!isValidCreatinineIf} fill="solid" onClick={onReCalculate}>{t('Recalculate')}</IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonSlide>
        </IonSlides>
      </IonContent>
    </IonPage>
  );
};

export default RenalRisk;
